import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { deleteSiteTitle, getSiteTitles } from '../../../../../api/endpoints/cmsSite';
import type { SiteTitle } from '../../../../../api/types/cmsSite';
import { ApiErrorAlert } from '../../../../../components/ApiErrorAlert';
import { ConfirmDialog } from '../../../../../components/ConfirmDialog';
import { SiteTitleFormDialog } from '../SiteTitleFormDialog';

/** List rendered as a plain MUI Table (not @mui/x-data-grid — incompatible with this
 *  project's pinned TypeScript 4.9.5). Confirmed via local-backend swagger + live curl (see
 *  api/types/cmsSite.ts header). SiteTitle is scoped to a parent Site via `site_Id`; this page
 *  is only ever rendered as a tab inside `SiteDetailPage` for one specific site, so `siteId`
 *  comes in as a fixed prop. */
export default function SiteTitleListPage({ siteId }: { siteId: number }) {
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<SiteTitle | null>(null);
  const [deleting, setDeleting] = useState<SiteTitle | null>(null);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const query = useQuery({
    queryKey: ['cms', 'siteTitles', siteId],
    queryFn: () => getSiteTitles(siteId),
  });
  const rows: SiteTitle[] = useMemo(
    () => [...(query.data?.siteTitles ?? [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    [query.data]
  );

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteSiteTitle(id),
    onSuccess: () => {
      enqueueSnackbar('Site başlığı silindi.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['cms', 'siteTitles'] });
      setDeleting(null);
    },
    onError: (err) => enqueueSnackbar(err instanceof Error ? err.message : 'İşlem başarısız.', { variant: 'error' }),
  });

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Site Başlıkları</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Yeni Başlık
        </Button>
      </Stack>

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}
      {query.isFetching && <CircularProgress size={24} sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Sıra</TableCell>
              <TableCell>Başlık</TableCell>
              <TableCell>Alt Başlık</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !query.isFetching && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Kayıtlı site başlığı bulunamadı.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.position ?? '—'}</TableCell>
                <TableCell>{row.title ?? '—'}</TableCell>
                <TableCell>{row.subTitle ?? '—'}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setEditing(row)} aria-label="Düzenle">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleting(row)} aria-label="Sil">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <SiteTitleFormDialog open={createOpen} siteTitle={null} siteId={siteId} onClose={() => setCreateOpen(false)} />
      <SiteTitleFormDialog open={!!editing} siteTitle={editing} siteId={siteId} onClose={() => setEditing(null)} />

      <ConfirmDialog
        open={!!deleting}
        title="Site başlığını sil"
        description={`"${deleting?.title ?? ''}" başlığını silmek istediğinize emin misiniz?`}
        loading={deleteMutation.isPending}
        onConfirm={() => deleting && deleteMutation.mutate(deleting.id)}
        onCancel={() => setDeleting(null)}
      />
    </Box>
  );
}
